import React, { useEffect, useState } from 'react';
import { FaTimes, FaBox } from 'react-icons/fa';
import { InternalFieldName, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { toMask } from '../../services/currency';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';
import {
  EstimateProduct,
  EstimateViewModel,
} from '../../models/estimate/EstimateViewModel';
import CustomModal from '../CustomModal/CustomModal';
import CustomButton from '../CustomButton/CustomButton';
import EstimateStatus from '../../enums/EstimateStatus';

interface EstimateProductsProps {
  estimate: EstimateViewModel;
  items: EstimateProduct[];
  refreshProducts: () => void;
  removeProduct: (estimateProduct: EstimateProduct) => void;
}

interface EstimateProductFormFields {
  product: number;
  quantity: number;
  isGrouped: boolean;
}

type ProductModel = {
  id: number;
  name: string;
  price: number;
  quantity: number;
  isGrouped: boolean;
};

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function EstimateProducts({
  estimate,
  items,
  refreshProducts,
  removeProduct,
}: EstimateProductsProps) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<EstimateProductFormFields>({ mode: 'onBlur' });

  const [isLoading, setIsLoading] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, any>>([]);
  const [products, setProducts] = useState<ProductModel[]>([]);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const {
    user: { organization },
  } = useAuth();

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  async function getProducts() {
    if (organization) {
      setIsLoading(true);
      setIsOpenModal(true);
      const response = await api.get(
        `organizations/${organization.id}/estimates/${estimate.id}/products/available`
      );
      // TODO: Remover os que já estão adicionados
      setProducts(response.data.data);
      setIsLoading(false);
    }
  }

  const onSubmit = async (data: EstimateProductFormFields) => {
    try {
      await api.post(
        `organizations/${organization.id}/estimates/${estimate.id}/products`,
        data
      );

      setProducts([]);
      setIsOpenModal(false);
      refreshProducts();
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  return (
    <>
      <span className='h2'>Produtos</span>
      <div className='table-responsive'>
        <table className='table'>
          <thead>
            <tr>
              <th>Nome</th>
              <th className='text-end'>Quantidade</th>
              <th className='text-end'>Valor</th>
              <th className='text-end'>Total</th>
              <th style={{ width: '20px' }}>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td className='text-end'>{item.quantity}</td>
                  <td className='text-end'>{toMask(item.price)}</td>
                  <td className='text-end'>
                    {toMask(item.quantity * item.price)}
                  </td>
                  <td>
                    {estimate.status !== EstimateStatus.Completed &&
                      estimate.status !== EstimateStatus.Canceled && (
                        <button
                          className='btn btn-sm btn-danger'
                          type='button'
                          onClick={() => removeProduct(item)}
                        >
                          <FaTimes />
                        </button>
                      )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>Nenhum registro</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className='text-end'>
        {estimate.status !== EstimateStatus.Completed &&
          estimate.status !== EstimateStatus.Canceled && (
            <button
              type='button'
              className='btn btn-secondary btn-sm'
              onClick={() => getProducts()}
            >
              Adicionar produto <FaBox />
            </button>
          )}

        <CustomModal
          modalTitle='Adicionar produto'
          isOpen={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          fullscreenOnMobile={false}
        >
          <form
            style={{
              paddingTop: '1rem',
              paddingBottom: '1rem',
            }}
          >
            <div className='mb-3'>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className='form-label'>Selecione o produto</label>
              <select
                required
                className='form-select'
                aria-label='Selecione o produto'
                {...register('product', { required: true })}
              >
                <option value=''>Selecione o produto</option>
                {products.map((product) => (
                  <option value={product.id} key={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
              {errors.product && (
                <div className='form-text text-danger'>
                  {errors.product.message}
                </div>
              )}
            </div>

            <div className='mb-3'>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor='inputLabel' className='form-label'>
                Quantidade
              </label>
              <input
                {...register('quantity', { required: true })}
                className='form-control'
                placeholder='Quantidade'
                type='number'
                min='1'
                step='1'
                required
              />
              {errors.quantity && (
                <div className='form-text text-danger'>
                  {errors.quantity.message}
                </div>
              )}
            </div>
            <div className='col-sm-3'>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  role='switch'
                  id='isGrouped'
                  {...register('isGrouped')}
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className='form-check-label' htmlFor='isGrouped'>
                  Agrupado
                </label>
              </div>
            </div>
            <div className='text-end mt-3'>
              <CustomButton
                type='button'
                className='btn btn-primary'
                handleClick={handleSubmit(onSubmit)}
              >
                Adicionar
              </CustomButton>
            </div>
          </form>
        </CustomModal>
      </div>
    </>
  );
}
